/**
 * Admin API Functions
 * Feature: 007-cloudflare-migration
 *
 * API functions for admin operations via Cloudflare API Gateway.
 * Uses PUBLIC endpoints (with RLS/admin access) for CRUD operations.
 * Uses /admin/fraud/* endpoints for fraud detection (the only true admin endpoints).
 *
 * Architecture Note:
 * - Admin users have full CRUD access via Supabase RLS policies
 * - Public endpoints like /businesses, /marketplace/listings work for admins
 * - No dedicated /admin/users, /admin/businesses, /admin/marketplace endpoints exist
 * - Metrics are computed client-side from list totals
 */

import { get, post, patch, put, del } from './client'
import type {
  // Metrics
  ApiMetrics,
  MetricType,
  // Users
  ApiUser,
  ApiUserListItem,
  UserListParams,
  UpdateUserRequest,
  PaginatedResponse,
  // Businesses
  ApiBusiness,
  ApiBusinessListItem,
  BusinessListParams,
  UpdateBusinessRequest,
  // Listings
  ApiListing,
  ApiListingListItem,
  ListingListParams,
  UpdateListingRequest,
  // Fraud
  ApiFraudStats,
  ApiFraudBlock,
  ApiFraudFlag,
  BlockRequest,
  ReviewRequest,
  FraudConfig,
} from './types'

// ============================================================================
// Backend Response Types (from public endpoints)
// ============================================================================

/** Pagination info from backend */
interface BackendPagination {
  page: number
  limit: number
  total: number
  total_pages: number
}

/** Profile response from /users/:id/profile */
interface ProfileResponse {
  data: {
    id: string
    display_name: string | null
    bio: string | null
    location_city: string | null
    avatar_url: string | null
    created_at: string
  }
  meta?: { cached: boolean }
}

/** Business list response from /businesses */
interface BusinessListResponse {
  businesses: BackendBusiness[]
  pagination: BackendPagination
}

/** Business detail from backend - matches actual database JSONB schema */
interface BackendBusiness {
  id: string
  created_by: string
  name: string
  description: string | null
  category: string | null  // UUID reference to category
  category_name?: string   // Resolved category name (if included by API)
  status: string
  city: string | null
  location: { lat: number; lng: number } | null
  photos: string[]
  tags: string[]
  rating_summary: {
    avg_rating: number
    review_count: number
    rating_distribution: Record<string, number>
  } | null
  created_at: string
  updated_at: string
}

/** Alias for detailed business response (same structure) */
type BusinessDetailResponse = BackendBusiness

/** Listing list response from /marketplace/listings */
interface ListingListResponse {
  listings: BackendListing[]
  pagination: BackendPagination
}

/** Listing from backend - matches current API Gateway response */
interface BackendListing {
  id: string
  user_id: string
  category_id: string
  category_name?: string | null
  title: string
  description: string | null
  price: number
  currency: 'USD' | 'KHR'
  city: string | null
  location: { lat: number; lng: number } | null
  status: string
  photos: string[]
  image_urls: string[]
  tags: string[]
  view_count: number
  contact_count: number
  offer_enabled: boolean
  offer_min: number | null
  offer_max: number | null
  created_at: string
  updated_at: string
  expires_at: string | null
}

/** Alias for detailed listing response (same structure) */
type ListingDetailResponse = BackendListing

/** Jobs list response from /jobs */
interface JobListResponse {
  jobs: unknown[]
  pagination: BackendPagination
}

/** User stats response from /admin/users/stats */
interface UserStatsResponse {
  success: boolean
  data: {
    total: number
    verified: number
    unverified: number
    deleted: number
  }
  cached?: boolean
}

// ============================================================================
// Response Transformers
// ============================================================================

/**
 * Transform profile response to ApiUser format
 */
function transformProfileToUser(profile: ProfileResponse['data']): ApiUser {
  return {
    id: profile.id,
    email: '', // Not available in public profile
    display_name: profile.display_name,
    avatar_url: profile.avatar_url,
    role: 'User', // Not available in public profile
    status: 'active', // Not available in public profile
    created_at: profile.created_at,
    updated_at: profile.created_at,
    last_sign_in_at: null,
  }
}

/**
 * Transform backend business to ApiBusiness format
 */
function transformBusinessDetailResponse(business: BackendBusiness): ApiBusiness {
  return {
    id: business.id,
    user_id: business.created_by,
    name: business.name,
    name_km: null,
    description: business.description,
    description_km: null,
    category: business.category || '',
    category_name: business.category_name,
    status: (business.status || 'active') as 'active' | 'disabled' | 'awaiting_moderation',
    city: business.city || null,
    location: business.location
      ? {
          lat: business.location.lat,
          lng: business.location.lng,
        }
      : null,
    photos: business.photos || [],
    tags: business.tags || [],
    created_at: business.created_at,
    updated_at: business.updated_at,
  }
}

/**
 * Transform backend listing to ApiListingListItem format
 */
function transformListingResponse(listing: BackendListing): ApiListingListItem {
  return {
    id: listing.id,
    title: listing.title,
    title_km: null, // Backend uses single title field
    category_id: listing.category_id,
    category_name: listing.category_name || null,
    price: listing.price,
    currency: listing.currency,
    status: listing.status as ApiListingListItem['status'],
    photos: listing.image_urls || listing.photos || [],
    created_at: listing.created_at,
    expires_at: listing.expires_at || undefined,
  }
}

/**
 * Transform backend listing to ApiListing format
 */
function transformListingDetailResponse(listing: BackendListing): ApiListing {
  return {
    id: listing.id,
    user_id: listing.user_id,
    seller_id: listing.user_id, // Alias
    user: undefined, // Would need separate user fetch
    title: listing.title,
    title_km: null,
    description: listing.description,
    description_km: null,
    category_id: listing.category_id,
    category_name: listing.category_name || null,
    category: undefined,
    price: listing.price,
    currency: listing.currency,
    condition: 'good', // Not in backend response
    status: listing.status as ApiListing['status'],
    photos: listing.image_urls || listing.photos || [],
    location: {
      city: listing.city || undefined,
      lat: listing.location?.lat,
      lng: listing.location?.lng,
    },
    latitude: listing.location?.lat,
    longitude: listing.location?.lng,
    views_count: listing.view_count,
    tags: listing.tags || [],
    offer_enabled: listing.offer_enabled,
    offer_min: listing.offer_min ?? undefined,
    offer_max: listing.offer_max ?? undefined,
    created_at: listing.created_at,
    updated_at: listing.updated_at,
    expires_at: listing.expires_at || undefined,
  }
}

// ============================================================================
// Admin Metrics (Client-Side Aggregation)
// ============================================================================
// Note: There are no /admin/metrics/* endpoints in the backend.
// Metrics are computed by fetching list totals from public endpoints.

/**
 * Get metrics for a specific entity type
 * Aggregates counts from public endpoints since no metrics API exists
 * @param type - Metric type (users, businesses, jobs, marketplace)
 */
export async function getMetrics(type: MetricType): Promise<ApiMetrics> {
  // Fetch from public endpoints and aggregate
  const now = new Date()
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
  const periodEnd = now.toISOString()

  switch (type) {
    case 'users': {
      // Fetch real user stats from admin endpoint
      const response = await get<UserStatsResponse>('/admin/users/stats')
      return {
        type: 'users',
        total: response.data.total,
        breakdown: {
          by_status: {
            verified: response.data.verified,
            unverified: response.data.unverified,
            deleted: response.data.deleted,
          },
        },
        period: { start: periodStart, end: periodEnd },
      }
    }
    case 'businesses': {
      const response = await get<BusinessListResponse>('/businesses', { limit: 1 })
      return {
        type: 'businesses',
        total: response.pagination?.total || 0,
        breakdown: { by_status: { active: response.pagination?.total || 0 } },
        period: { start: periodStart, end: periodEnd },
      }
    }
    case 'jobs': {
      const response = await get<JobListResponse>('/jobs/listings', { limit: 1 })
      return {
        type: 'jobs',
        total: response.pagination?.total || 0,
        breakdown: { by_status: { active: response.pagination?.total || 0 } },
        period: { start: periodStart, end: periodEnd },
      }
    }
    case 'marketplace': {
      const response = await get<ListingListResponse>('/marketplace/listings', { limit: 1 })
      return {
        type: 'marketplace',
        total: response.pagination?.total || 0,
        breakdown: { by_status: { active: response.pagination?.total || 0 } },
        period: { start: periodStart, end: periodEnd },
      }
    }
    default:
      throw new Error(`Unknown metric type: ${type}`)
  }
}

/**
 * Get all metrics in parallel
 * Returns an object with all four metric types
 */
export async function getAllMetrics(): Promise<Record<MetricType, ApiMetrics | null>> {
  const types: MetricType[] = ['users', 'businesses', 'jobs', 'marketplace']

  const results = await Promise.allSettled(types.map((type) => getMetrics(type)))

  const metrics: Record<MetricType, ApiMetrics | null> = {
    users: null,
    businesses: null,
    jobs: null,
    marketplace: null,
  }

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      metrics[types[index]] = result.value
    }
  })

  return metrics
}

/** User list response from /admin/users */
interface UserListResponse {
  success: boolean
  data: {
    users: Array<{
      id: string
      email: string
      display_name: string | null
      avatar_url: string | null
      auth_provider: string
      role: string
      roles: string[]
      status: string
      is_verified: boolean
      created_at: string
      last_login_at: string | null
      // Better Auth ban fields
      ban_reason?: string | null
      ban_expires?: string | null
    }>
    pagination: {
      page: number
      limit: number
      total: number
      total_pages: number
    }
  }
}

// ============================================================================
// User Administration (via /admin/users endpoint)
// ============================================================================

/**
 * List users with pagination and filters
 * Uses admin users endpoint
 */
export async function listUsers(
  params?: UserListParams
): Promise<PaginatedResponse<ApiUserListItem>> {
  const queryParams: Record<string, string | number | undefined> = {
    limit: params?.limit || 20,
    page: 1,
  }

  if (params?.q) {
    queryParams.q = params.q
  }
  if (params?.status) {
    queryParams.status = params.status
  }

  const response = await get<UserListResponse>('/admin/users', queryParams)

  return {
    data: response.data.users.map((user) => ({
      id: user.id,
      email: user.email,
      display_name: user.display_name,
      avatar_url: user.avatar_url,
      role: user.role as 'Admin' | 'User',
      status: user.status as 'active' | 'banned' | 'suspended' | 'deleted',
      created_at: user.created_at,
      is_verified: user.is_verified,
      ban_reason: user.ban_reason ?? null,
      ban_expires: user.ban_expires ?? null,
    })),
    pagination: {
      cursor: null,
      hasMore: response.data.pagination.page < response.data.pagination.total_pages,
      total: response.data.pagination.total,
    },
  }
}

/** Admin user detail response from /admin/users/:id */
interface AdminUserDetailResponse {
  success: boolean
  data: {
    id: string
    email: string
    display_name: string | null
    avatar_url: string | null
    auth_provider: string
    role: string
    roles: string[]
    status: string
    is_verified: boolean
    created_at: string
    last_login_at: string | null
    // Better Auth ban fields
    ban_reason?: string | null
    ban_expires?: string | null
    phone?: string | null
  }
}

/** Admin linked content response */
interface AdminLinkedContentResponse {
  success: boolean
  data: {
    count: number
    items: Array<{
      id: string
      name?: string
      title?: string
      status: string
      created_at: string
    }>
  }
}

/**
 * Get user details by ID (admin endpoint)
 * Uses admin endpoint with full user details including roles
 */
export async function getUser(id: string): Promise<ApiUser> {
  const response = await get<AdminUserDetailResponse>(`/admin/users/${id}`)
  return {
    id: response.data.id,
    email: response.data.email,
    display_name: response.data.display_name,
    avatar_url: response.data.avatar_url,
    role: response.data.role as 'Admin' | 'User',
    status: response.data.status as 'active' | 'banned' | 'suspended' | 'deleted',
    created_at: response.data.created_at,
    updated_at: response.data.created_at,
    last_sign_in_at: response.data.last_login_at,
    // Include Better Auth fields
    roles: response.data.roles,
    is_verified: response.data.is_verified,
    ban_reason: response.data.ban_reason ?? null,
    ban_expires: response.data.ban_expires ?? null,
    phone: response.data.phone ?? null,
  } as ApiUser
}

/**
 * Get user's businesses (admin endpoint)
 */
export async function getUserBusinesses(userId: string, limit = 10): Promise<AdminLinkedContentResponse['data']> {
  const response = await get<AdminLinkedContentResponse>(`/admin/users/${userId}/businesses`, { limit })
  return response.data
}

/**
 * Get user's marketplace items (admin endpoint)
 */
export async function getUserMarketplace(userId: string, limit = 10): Promise<AdminLinkedContentResponse['data']> {
  const response = await get<AdminLinkedContentResponse>(`/admin/users/${userId}/marketplace`, { limit })
  return response.data
}

/**
 * Get user's job advertisements (admin endpoint)
 */
export async function getUserJobs(userId: string, limit = 10): Promise<AdminLinkedContentResponse['data']> {
  const response = await get<AdminLinkedContentResponse>(`/admin/users/${userId}/jobs`, { limit })
  return response.data
}

/**
 * Update user (role, status)
 * Uses admin status endpoint for status changes
 */
export async function updateUser(
  id: string,
  data: UpdateUserRequest
): Promise<ApiUser> {
  // Use admin status endpoint for status changes
  if (data.status) {
    const response = await patch<AdminUserDetailResponse>(`/admin/users/${id}/status`, { status: data.status })
    return {
      id: response.data.id,
      email: response.data.email,
      display_name: response.data.display_name,
      avatar_url: response.data.avatar_url,
      role: response.data.role as 'Admin' | 'User',
      status: response.data.status as 'active' | 'banned' | 'suspended' | 'deleted',
      created_at: response.data.created_at,
      updated_at: response.data.created_at,
      last_sign_in_at: response.data.last_login_at,
      roles: response.data.roles,
      is_verified: response.data.is_verified,
      ban_reason: response.data.ban_reason ?? null,
      ban_expires: response.data.ban_expires ?? null,
    } as ApiUser
  }
  
  // For other updates, use profile endpoint
  const response = await patch<ProfileResponse>(`/users/${id}/profile`, data)
  return transformProfileToUser(response.data)
}

/**
 * Ban a user account
 * Uses PATCH /admin/users/:id/status with status: 'banned'
 * @param id - User ID
 * @param reason - Optional reason for the ban
 */
export async function banUser(id: string, reason?: string): Promise<ApiUser> {
  const response = await patch<AdminUserDetailResponse>(`/admin/users/${id}/status`, { 
    status: 'banned',
    reason,
  })
  return {
    id: response.data.id,
    email: response.data.email,
    display_name: response.data.display_name,
    avatar_url: response.data.avatar_url,
    role: response.data.role as 'Admin' | 'User',
    status: 'banned',
    created_at: response.data.created_at,
    updated_at: response.data.created_at,
    last_sign_in_at: response.data.last_login_at,
    roles: response.data.roles,
    is_verified: response.data.is_verified,
    ban_reason: reason ?? null,
    ban_expires: response.data.ban_expires ?? null,
  } as ApiUser
}

/**
 * Unban a user account
 * Uses PATCH /admin/users/:id/status with status: 'active'
 * @param id - User ID
 */
export async function unbanUser(id: string): Promise<ApiUser> {
  const response = await patch<AdminUserDetailResponse>(`/admin/users/${id}/status`, { 
    status: 'active',
  })
  return {
    id: response.data.id,
    email: response.data.email,
    display_name: response.data.display_name,
    avatar_url: response.data.avatar_url,
    role: response.data.role as 'Admin' | 'User',
    status: 'active',
    created_at: response.data.created_at,
    updated_at: response.data.created_at,
    last_sign_in_at: response.data.last_login_at,
    roles: response.data.roles,
    is_verified: response.data.is_verified,
    ban_reason: null,
    ban_expires: null,
  } as ApiUser
}

// ============================================================================
// Business Administration (via /admin/businesses endpoint)
// ============================================================================

/** Admin business list response from /admin/businesses - matches JSONB schema */
interface AdminBusinessListResponse {
  success: boolean
  data: {
    businesses: Array<{
      id: string
      name: string
      description: string | null
      category: string | null
      category_name?: string
      status: string
      city: string | null
      location: { lat: number; lng: number } | null
      photo_url: string | null
      photos: string[]
      tags: string[]
      created_by: string
      created_at: string
      updated_at: string
    }>
    pagination: {
      page: number
      limit: number
      total: number
      total_pages: number
    }
  }
}

/**
 * List businesses with pagination and filters
 * Uses /admin/businesses endpoint for full admin access
 */
export async function listBusinesses(
  params?: BusinessListParams
): Promise<PaginatedResponse<ApiBusinessListItem>> {
  const queryParams: Record<string, string | number | undefined> = {
    limit: params?.limit || 50,
    page: 1,
  }

  if (params?.q) {
    queryParams.q = params.q
  }
  if (params?.status) {
    queryParams.status = params.status
  }
  if (params?.category) {
    queryParams.category = params.category
  }

  const response = await get<AdminBusinessListResponse>('/admin/businesses', queryParams)
  
  return {
    data: response.data.businesses.map((business) => ({
      id: business.id,
      name: business.name,
      name_km: null,
      category: business.category || '',
      category_name: business.category_name,
      city: business.city || null,
      status: business.status as 'active' | 'disabled' | 'awaiting_moderation',
      photos: business.photos,
      created_at: business.created_at,
    })),
    pagination: {
      cursor: null,
      hasMore: response.data.pagination.page < response.data.pagination.total_pages,
      total: response.data.pagination.total,
    },
  }
}

/**
 * Get business details by ID
 */
export async function getBusiness(id: string): Promise<ApiBusiness> {
  const response = await get<BusinessDetailResponse>(`/businesses/${id}`)
  return transformBusinessDetailResponse(response)
}

/** Admin business status update response - matches JSONB schema */
interface AdminBusinessStatusResponse {
  success: boolean
  data: {
    id: string
    name: string
    description: string | null
    category: string | null
    status: string
    city: string | null
    location: { lat: number; lng: number } | null
    photo_url: string | null
    photos: string[]
    tags: string[]
    created_by: string
    created_at: string
    updated_at: string
  }
}

/**
 * Update business status (admin endpoint)
 * Uses PATCH /admin/businesses/:id/status
 */
export async function updateBusinessStatus(
  id: string,
  status: string,
  reason?: string
): Promise<ApiBusiness> {
  const response = await patch<AdminBusinessStatusResponse>(`/admin/businesses/${id}/status`, { status, reason })
  
  return {
    id: response.data.id,
    user_id: response.data.created_by,
    name: response.data.name,
    name_km: null,
    description: response.data.description,
    description_km: null,
    category: response.data.category || '',
    status: response.data.status as 'active' | 'disabled' | 'awaiting_moderation',
    city: response.data.city,
    location: response.data.location,
    photos: response.data.photos || [],
    tags: response.data.tags || [],
    created_at: response.data.created_at,
    updated_at: response.data.updated_at,
  }
}

/**
 * Update business (profile fields only)
 * Uses PATCH /businesses/:id (owner can update via RLS)
 */
export async function updateBusiness(
  id: string,
  data: UpdateBusinessRequest
): Promise<ApiBusiness> {
  // If status is being updated, use the admin endpoint
  if (data.status) {
    return updateBusinessStatus(id, data.status)
  }
  
  const response = await patch<BusinessDetailResponse>(`/businesses/${id}`, data)
  return transformBusinessDetailResponse(response)
}

// ============================================================================
// Marketplace Administration (via Public Marketplace Endpoint)
// ============================================================================

/**
 * List marketplace listings with pagination and filters
 * Uses /admin/marketplace endpoint for admin access with status filter support
 */
export async function listListings(
  params?: ListingListParams
): Promise<PaginatedResponse<ApiListingListItem>> {
  const queryParams: Record<string, string | number | undefined> = {
    limit: params?.limit,
    page: params?.cursor ? undefined : 1,
  }

  if (params?.q) {
    queryParams.q = params.q
  }
  if (params?.category) {
    queryParams.category_id = params.category
  }
  if (params?.status) {
    queryParams.status = params.status
  }

  // Use admin endpoint for full access including status filter
  const response = await get<ListingListResponse>('/admin/marketplace', queryParams)
  
  return {
    data: response.listings.map(transformListingResponse),
    pagination: {
      cursor: null,
      hasMore: response.pagination.page < response.pagination.total_pages,
      total: response.pagination.total,
    },
  }
}

/**
 * Get listing details by ID
 */
export async function getListing(id: string): Promise<ApiListing> {
  const response = await get<ListingDetailResponse>(`/marketplace/listings/${id}`)
  return transformListingDetailResponse(response)
}

/**
 * Update listing (moderation actions)
 * Uses PATCH /marketplace/listings/:id (admin can update via RLS)
 */
export async function updateListing(
  id: string,
  data: UpdateListingRequest
): Promise<ApiListing> {
  const response = await patch<ListingDetailResponse>(`/marketplace/listings/${id}`, data)
  return transformListingDetailResponse(response)
}

// ============================================================================
// Categories (via /categories endpoint)
// ============================================================================

/** Category from backend */
interface BackendCategory {
  id: string
  parent_id: string | null
  name_en: string
  name_km: string
  name_fr: string
  slug: string
  icon_url: string | null
  sort_order: number
  feature?: string | null
}

/** Categories list response */
interface CategoriesListResponse {
  categories: BackendCategory[]
}

/**
 * Get categories, optionally filtered by feature
 * @param feature - Filter by feature (marketplace, jobs, businesses)
 */
export async function getCategories(feature?: string): Promise<BackendCategory[]> {
  const queryParams: Record<string, string | undefined> = {}
  if (feature) {
    queryParams.feature = feature
  }
  
  const response = await get<CategoriesListResponse>('/categories', queryParams)
  return response.categories
}

// ============================================================================
// Fraud Detection Administration (via /admin/fraud/* endpoints)
// ============================================================================
// Note: These are the ONLY true admin endpoints in the API Gateway.
// They are protected by JWT + admin role verification.

/**
 * Get fraud detection statistics
 */
export async function getFraudStats(): Promise<ApiFraudStats> {
  const response = await get<{ success: boolean; data: ApiFraudStats }>('/admin/fraud/stats')
  return response.data
}

/**
 * List all blocked users/IPs
 */
export async function listBlocked(): Promise<ApiFraudBlock[]> {
  const response = await get<{ success: boolean; data: ApiFraudBlock[]; count: number }>(
    '/admin/fraud/blocked'
  )
  return response.data
}

/**
 * Block a user or IP
 */
export async function blockEntity(request: BlockRequest): Promise<void> {
  await post<{ success: boolean }>('/admin/fraud/block', request)
}

/**
 * Unblock a user or IP
 */
export async function unblockEntity(type: 'user' | 'ip', id: string): Promise<void> {
  await del<{ success: boolean }>(`/admin/fraud/block/${type}/${id}`)
}

/**
 * List flagged (suspicious) users/requests
 */
export async function listFlagged(): Promise<ApiFraudFlag[]> {
  const response = await get<{ success: boolean; data: ApiFraudFlag[]; count: number }>(
    '/admin/fraud/flagged'
  )
  return response.data
}

/**
 * Review and clear a flagged item
 */
export async function reviewFlag(request: ReviewRequest): Promise<void> {
  await post<{ success: boolean }>('/admin/fraud/review', request)
}

/**
 * Get fraud detection configuration
 */
export async function getFraudConfig(): Promise<FraudConfig> {
  const response = await get<{ success: boolean; data: FraudConfig }>('/admin/fraud/config')
  return response.data
}

/**
 * Update fraud detection configuration
 */
export async function updateFraudConfig(config: Partial<FraudConfig>): Promise<FraudConfig> {
  const response = await put<{ success: boolean; data: FraudConfig }>('/admin/fraud/config', config)
  return response.data
}
